/**
 * The built-in sheet starters, as an administrator sees them (ADR-0013, 12.2).
 *
 * Beside `AdminModules` and shaped like it, because the mechanism is the same: what
 * ships is repo data reviewed in git, and the only thing this page can do is switch
 * an entry off for this instance. There is no "add", no "edit", and no upload --
 * an administrator who could author a starter here would be authoring sheet content
 * outside review, and "starters are structure, not calculations" would rest on
 * whoever last used this page.
 *
 * Nor is there a verification column. A starter carries no engineering claim, and a
 * column a reader could take for one is the middle trust tier ADR-0008 refuses.
 */
import { useTranslation } from 'react-i18next'

import { useAdminSheetTemplates, useUpdateAdminSheetTemplate } from '@/api/admin'
import { ApiError } from '@/api/client'
import { TableScroller, Td, Th } from '@/components/admin/AdminShell'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'

export function AdminSheetTemplates() {
  const { t } = useTranslation(['admin', 'sheets', 'common'])
  const { toast } = useToast()

  const starters = useAdminSheetTemplates()
  const update = useUpdateAdminSheetTemplate()

  if (starters.isLoading) return <Skeleton className="h-64" />

  const rows = starters.data ?? []

  const toggle = (id: string, enabled: boolean) =>
    update.mutate(
      { id, enabled },
      {
        onSuccess: () => toast({ title: t('common:common.saved'), variant: 'success' }),
        onError: (error) =>
          toast({
            title: t('common:common.error'),
            description: error instanceof ApiError ? error.localised : undefined,
            variant: 'error',
          }),
      },
    )

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('admin:admin.sheet_templates.title')}</h2>
      <p className="text-sm text-text-muted">{t('admin:admin.sheet_templates.hint')}</p>
      <p className="rounded-md border border-[var(--status-warn)] bg-[var(--status-warn-bg)] p-3 text-sm text-text">
        {t('admin:admin.sheet_templates.firewall')}
      </p>

      {rows.length === 0 ? (
        <p className="rounded-lg border border-border p-6 text-center text-sm text-text-muted">
          {t('admin:admin.sheet_templates.empty')}
        </p>
      ) : (
        <TableScroller>
          <thead>
            <tr>
              <Th>{t('admin:admin.sheet_templates.starter')}</Th>
              <Th>{t('admin:admin.sheet_templates.category')}</Th>
              <Th className="text-end">{t('admin:admin.sheet_templates.cells')}</Th>
              <Th className="text-end">{t('admin:admin.sheet_templates.enabled')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <Td>
                  <div className="font-medium">{t(row.title_key, { ns: 'sheets' })}</div>
                  <div className="text-xs text-text-muted">
                    {t(row.description_key, { ns: 'sheets' })}
                  </div>
                  {/* The id is an identifier, never translated (14.4). */}
                  <div className="font-mono text-xs text-text-muted">{row.id}</div>
                </Td>
                <Td>
                  {t(`sheets:sheets.templates.category.${row.category}`, {
                    defaultValue: row.category,
                  })}
                </Td>
                <Td className="text-end tabular-nums">{row.cell_count}</Td>
                <Td>
                  <div className="flex items-center justify-end">
                    <input
                      type="checkbox"
                      aria-label={t('admin:admin.sheet_templates.enabled')}
                      checked={row.enabled}
                      onChange={(event) => toggle(row.id, event.target.checked)}
                    />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </TableScroller>
      )}
    </div>
  )
}
